import { useState, useCallback, useMemo, useEffect } from "react";
import {
  ChevronDown,
  Check,
  CircleDashed,
  CircleHelp,
  Image as ImageIcon,
  Loader2,
  X,
} from "lucide-react";

import { Button } from "@/modern/components/ui/button-shadcn";
import { Badge } from "@/modern/components/ui/badge";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "@/modern/components/ui/collapsible";
import { cn } from "@/modern/utils";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/modern/components/ui/tooltip";
import { useConfirm } from "@omit/react-confirm-dialog";
import { LightboxProvider, LightboxImageTrigger } from "@/modern/components/ui/lightbox";
import { useCoverage } from "../context/CoverageContext";
import {
  problemHubStatusBadgeClass,
  sequenceBadgeClassFromStatus,
  SEQUENCE_STATUS_LABELS,
  BUCKET_STATUS_LABELS,
  TEMPORAL_PHASES,
} from "../constants";
import { DEMOTE_ASSET_LEAVE_COVERAGE_NOTE } from "@/react/components/entityViews/marketing/api/marketingAssets.constants";
import type { BucketFill, BucketStatus, CoverageAsset } from "../types";

const StatusBadgeInline = ({ status, label }: { status: BucketStatus; label: string }) => (
  <Badge variant="secondary" size="sm" className={problemHubStatusBadgeClass(status)}>
    {label}
  </Badge>
);

export const HubDetailPanel = () => {
  const { selectedHub, selectedHubLoading, selectedHubFetching } = useCoverage();

  if (!selectedHub) return null;

  if (selectedHubLoading) {
    return (
      <div className="flex h-full flex-col overflow-hidden bg-card">
        <div className="shrink-0 px-5 pt-4 pb-3.5 border-b border-border">
          <p className="text-xs font-normal text-muted-foreground leading-none tracking-tight">
            Problem hub
          </p>
          <h3 className="truncate text-base font-normal text-foreground leading-snug tracking-tight mt-1.5">
            {selectedHub.name}
          </h3>
        </div>
        <div className="flex flex-1 items-center justify-center text-muted-foreground">
          <Loader2 className="size-5 animate-spin" aria-label="Loading hub detail" />
        </div>
      </div>
    );
  }

  const temporalCounts = TEMPORAL_PHASES.map((phase) => ({
    phase,
    count: selectedHub.temporalAssets[phase].length,
    assets: selectedHub.temporalAssets[phase],
  }));

  return (
    <div className="flex flex-col h-full overflow-hidden bg-card">
      <div className="shrink-0 px-5 pt-4 pb-3.5 border-b border-border">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-xs font-normal text-muted-foreground leading-none tracking-tight">
              Problem hub
            </p>
            <Tooltip>
              <TooltipTrigger asChild>
                <h3 className="truncate text-base font-normal text-foreground leading-snug tracking-tight mt-1.5 cursor-default">
                  {selectedHub.name}
                </h3>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                align="start"
                sideOffset={6}
                className="max-w-[280px] space-y-1 px-3 py-2"
              >
                <p className="font-medium text-popover-foreground">{selectedHub.name}</p>
                {selectedHub.description && (
                  <p className="text-muted-foreground">{selectedHub.description}</p>
                )}
              </TooltipContent>
            </Tooltip>
          </div>
          {selectedHubFetching && (
            <Loader2
              className="size-3.5 shrink-0 animate-spin text-muted-foreground/50 mt-1"
              aria-label="Refreshing hub detail"
            />
          )}
        </div>
      </div>

      <div
        className={cn(
          "flex-1 min-h-0 overflow-y-auto px-4 py-3.5 bg-background transition-opacity",
          selectedHubFetching && "opacity-70"
        )}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1">
            <p className="text-sm font-medium text-muted-foreground">Temporal</p>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="inline-flex size-5 shrink-0 items-center justify-center rounded text-muted-foreground/50 transition-colors hover:bg-muted hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label="What is temporal sequence?"
                >
                  <CircleHelp className="size-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="right"
                align="start"
                sideOffset={6}
                className="max-w-[240px] text-left border"
              >
                <p className="text-xs font-medium mb-1">Sequence readiness</p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Tracks whether your hub has before, during, and after assets — the three temporal
                  phases needed to produce credible sequence outputs like before/after comparisons
                  or repair progressions.
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
          <Badge
            variant="secondary"
            size="sm"
            className={sequenceBadgeClassFromStatus(selectedHub.sequenceStatus)}
          >
            {SEQUENCE_STATUS_LABELS[selectedHub.sequenceStatus]}
          </Badge>
        </div>
        <div className="flex flex-col gap-1">
          {temporalCounts.map(({ phase, count, assets }) => (
            <SequencePhaseCollapsible
              key={phase}
              phase={phase}
              totalCount={count}
              present={count > 0}
              assets={assets}
            />
          ))}
        </div>

        <div className="mt-5">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1">
              <p className="text-sm font-medium text-muted-foreground">Content roles</p>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex size-5 shrink-0 items-center justify-center rounded text-muted-foreground/50 transition-colors hover:bg-muted hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label="What are content roles?"
                  >
                    <CircleHelp className="size-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="right"
                  align="start"
                  sideOffset={6}
                  className="max-w-[240px] text-left border"
                >
                  <p className="text-xs font-medium mb-1">Content strength</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Each content role is a required category of visual evidence. Hub strength
                    reflects the percentage of roles that have at least one asset.
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
            <StatusBadgeInline
              status={selectedHub.overallStatus}
              label={BUCKET_STATUS_LABELS[selectedHub.overallStatus]}
            />
          </div>
          <div className="flex flex-col gap-1">
            {selectedHub.buckets.map((bucket) => (
              <BucketCollapsible key={bucket.tagId} bucket={bucket} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const SequencePhaseCollapsible = ({
  phase,
  totalCount,
  present,
  assets,
}: {
  phase: (typeof TEMPORAL_PHASES)[number];
  totalCount: number;
  present: boolean;
  assets: CoverageAsset[];
}) => {
  const [open, setOpen] = useState(false);
  const { demoteAsset, mutationBusy } = useCoverage();
  const hasAssets = totalCount > 0;
  const phaseLabel = phase.charAt(0).toUpperCase() + phase.slice(1);

  const [assetUrls, setAssetUrls] = useState<Map<number, string>>(new Map());

  const handleUrlResolved = useCallback((idx: number, url: string) => {
    setAssetUrls((prev) => {
      if (prev.get(idx) === url) return prev;
      const next = new Map(prev);
      next.set(idx, url);
      return next;
    });
  }, []);

  const lightboxImages = useMemo(
    () =>
      assets.map((asset, i) => ({
        src: assetUrls.get(i) || asset.thumbnailUrl || asset.url || "",
        alt: asset.altText || asset.caption || "",
      })),
    [assets, assetUrls]
  );

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="w-full flex items-center gap-2.5 px-2.5 py-2 bg-transparent border-none cursor-pointer text-left transition-colors hover:bg-accent/50"
          >
            <SequenceIndicator passed={present} />
            <span className="flex-1 min-w-0 text-xs font-medium text-foreground capitalize">
              {phase}
            </span>
            <span
              className={cn(
                "text-xs font-semibold min-w-3.5 text-right",
                hasAssets ? "text-foreground" : "text-amber-500/70"
              )}
            >
              {totalCount}
            </span>
            <ChevronDown
              className={cn(
                "shrink-0 size-3.5 text-muted-foreground/60 transition-transform",
                open && "rotate-180"
              )}
            />
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-2.5 pt-2 pb-2.5 border-t border-border">
            {hasAssets ? (
              <LightboxProvider images={lightboxImages}>
                <div className="flex flex-wrap gap-1.5">
                  {assets.map((asset, i) => (
                    <AssetThumbnail
                      key={asset.id}
                      asset={asset}
                      index={i}
                      onUrlResolved={handleUrlResolved}
                      removeLabel="Demote this image?"
                      removeDescription={`Removing it from ${phaseLabel} demotes the asset. ${DEMOTE_ASSET_LEAVE_COVERAGE_NOTE}`}
                      removeActionLabel="Demote"
                      disabled={mutationBusy}
                      onRemove={() => demoteAsset(asset.id)}
                    />
                  ))}
                </div>
              </LightboxProvider>
            ) : (
              <p className="text-xs text-muted-foreground/60">No {phase} assets yet.</p>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

const SequenceIndicator = ({ passed }: { passed: boolean }) => (
  <div
    className={cn(
      "size-4 rounded-full shrink-0 flex items-center justify-center",
      passed ? "bg-success/15" : "bg-amber-500/15"
    )}
  >
    {passed ? (
      <Check className="size-2.5 text-success" />
    ) : (
      <CircleDashed className="size-2.5 text-amber-500/70" />
    )}
  </div>
);

const BucketCollapsible = ({ bucket }: { bucket: BucketFill }) => {
  const [open, setOpen] = useState(false);
  const { removeContentRole, mutationBusy } = useCoverage();
  const hasAssets = bucket.assetCount > 0;

  const [assetUrls, setAssetUrls] = useState<Map<number, string>>(new Map());

  const handleUrlResolved = useCallback((idx: number, url: string) => {
    setAssetUrls((prev) => {
      if (prev.get(idx) === url) return prev;
      const next = new Map(prev);
      next.set(idx, url);
      return next;
    });
  }, []);

  const lightboxImages = useMemo(
    () =>
      bucket.assets.map((asset, i) => ({
        src: assetUrls.get(i) || asset.thumbnailUrl || asset.url || "",
        alt: asset.altText || asset.caption || "",
      })),
    [bucket.assets, assetUrls]
  );

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="w-full flex items-center gap-2.5 px-2.5 py-2 bg-transparent border-none cursor-pointer text-left transition-colors hover:bg-accent/50"
          >
            <SequenceIndicator passed={hasAssets} />
            <span className="flex-1 min-w-0 text-xs font-medium text-foreground">
              {bucket.label}
            </span>
            <span
              className={cn(
                "text-xs font-semibold min-w-3.5 text-right",
                hasAssets ? "text-success" : "text-amber-500/70"
              )}
            >
              {bucket.assetCount}
            </span>
            <ChevronDown
              className={cn(
                "shrink-0 size-3.5 text-muted-foreground/60 transition-transform",
                open && "rotate-180"
              )}
            />
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-2.5 pb-2.5 border-t border-border">
            {hasAssets ? (
              <LightboxProvider images={lightboxImages}>
                <div className="flex flex-wrap gap-1.5 mt-2.5">
                  {bucket.assets.map((asset, i) => (
                    <AssetThumbnail
                      key={asset.id}
                      asset={asset}
                      index={i}
                      onUrlResolved={handleUrlResolved}
                      removeLabel={`Remove from ${bucket.label}`}
                      removeDescription={`Remove this asset from the ${bucket.label} role?`}
                      removeActionLabel="Remove"
                      disabled={mutationBusy}
                      onRemove={() => removeContentRole(asset.id, bucket.tagId)}
                    />
                  ))}
                </div>
              </LightboxProvider>
            ) : (
              <p className="text-xs text-muted-foreground/60 mt-2">No assets in this role yet.</p>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

interface AssetThumbnailProps {
  asset: CoverageAsset;
  index: number;
  onRemove: () => void | Promise<void>;
  removeLabel: string;
  removeDescription: string;
  removeActionLabel: string;
  disabled?: boolean;
  onUrlResolved?: (idx: number, url: string) => void;
}

const AssetThumbnail = ({
  asset,
  index,
  onRemove,
  removeLabel,
  removeDescription,
  removeActionLabel,
  disabled,
  onUrlResolved,
}: AssetThumbnailProps) => {
  const confirm = useConfirm();
  const thumbnailUrl = asset.thumbnailUrl || asset.url || "";

  useEffect(() => {
    if (thumbnailUrl) {
      onUrlResolved?.(index, thumbnailUrl);
    }
  }, [thumbnailUrl, index, onUrlResolved]);

  const handleRemoveClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    const confirmed = await confirm({
      title: removeLabel,
      description: removeDescription,
      customActions: ({
        confirm: doConfirm,
        cancel,
      }: {
        confirm: () => void;
        cancel: () => void;
      }) => (
        <div className="space-x-4">
          <Button variant="ghost" onClick={cancel}>
            Cancel
          </Button>
          <Button
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={doConfirm}
          >
            {removeActionLabel}
          </Button>
        </div>
      ),
    });
    if (confirmed) await onRemove();
  };

  return (
    <div className="relative group size-14 shrink-0">
      <LightboxImageTrigger asChild index={index}>
        <div className="size-full rounded-md border border-border bg-muted overflow-hidden cursor-pointer">
          {thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt={asset.altText || asset.caption || ""}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImageIcon className="size-4 text-muted-foreground" />
            </div>
          )}
        </div>
      </LightboxImageTrigger>

      <button
        type="button"
        onClick={handleRemoveClick}
        disabled={disabled}
        className="absolute -top-1.5 -right-1.5 z-10 flex size-4 items-center justify-center rounded-full bg-destructive text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40"
        aria-label="Remove asset"
      >
        <X className="size-2.5" />
      </button>
    </div>
  );
};
